import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Newspaper, Volume2, TrendingUp } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { getDailyArticles, getAllArticles, blogCategories } from "@/data/blogPosts";

// Category icons and colors
const categoryConfig = {
  "All": { color: "#2DD4BF", icon: Newspaper },
  "Pharma News": { color: "#3B82F6", icon: TrendingUp },
  "Drug Knowledge": { color: "#A78BFA", icon: null },
  "Research": { color: "#F59E0B", icon: null },
  "India Pharma": { color: "#10B981", icon: null },
};

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");

  const dailyPosts = useMemo(() => getDailyArticles(), []);
  const allPosts = useMemo(() => getAllArticles(), []);

  // Show daily selection for "All", full category for filtered
  const displayPosts = activeCategory === "All"
    ? dailyPosts
    : allPosts.filter(p => p.category === activeCategory);

  return (
    <div data-testid="blog-page" className="min-h-screen pt-16 md:pt-0">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-4">
              <Newspaper size={12} className="text-[#2DD4BF]" />
              <span className="text-[10px] font-semibold text-[#94A3B8] tracking-wider uppercase">Updated Daily</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3">Pharma Insights</h1>
            <p className="text-[#94A3B8] max-w-2xl text-[15px] leading-relaxed">
              Daily pharmaceutical news, salt knowledge, research updates, and industry insights from India and the world.
            </p>
            
            {/* Voice Feature Highlight */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-[#2DD4BF]/5 border border-[#2DD4BF]/20"
            >
              <Volume2 size={14} className="text-[#2DD4BF]" />
              <span className="text-xs text-[#2DD4BF]">All articles available in audio</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="flex flex-wrap gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {blogCategories.map((cat, index) => {
              const config = categoryConfig[cat] || { color: "#2DD4BF" };
              return (
                <motion.button
                  key={cat}
                  data-testid={`blog-category-${cat.toLowerCase().replace(/\s/g,'-')}`}
                  onClick={() => setActiveCategory(cat)}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
                    activeCategory === cat
                      ? "text-[#022C22] shadow-lg"
                      : "bg-white/5 text-[#94A3B8] hover:bg-white/10 border border-white/5"
                  }`}
                  style={activeCategory === cat ? { backgroundColor: config.color } : {}}
                >
                  {cat}
                </motion.button>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Posts */}
      <section className="px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          {/* Featured post */}
          {displayPosts.length > 0 && (
            <ScrollReveal>
              <Link
                to={`/blog/${displayPosts[0].id}`}
                state={{ post: displayPosts[0] }}
                data-testid={`blog-featured-${displayPosts[0].id}`}
                className="group block mb-10"
              >
                <motion.div 
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 rounded-xl border border-white/5 bg-white/[0.02] hover:border-white/10 transition-all duration-500"
                  whileHover={{ borderColor: "rgba(45, 212, 191, 0.2)" }}
                >
                  <div className="aspect-[16/10] rounded-lg overflow-hidden bg-[#0F172A] relative">
                    <img
                      src={displayPosts[0].image}
                      alt={displayPosts[0].title}
                      className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    {/* Audio badge */}
                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm">
                      <Volume2 size={12} className="text-[#2DD4BF]" />
                      <span className="text-[10px] text-white font-medium">Audio Available</span>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center py-2">
                    <div className="flex items-center gap-3 mb-4">
                      <span 
                        className="text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full"
                        style={{ 
                          backgroundColor: `${categoryConfig[displayPosts[0].category]?.color || "#2DD4BF"}15`,
                          color: categoryConfig[displayPosts[0].category]?.color || "#2DD4BF"
                        }}
                      >
                        {displayPosts[0].category}
                      </span>
                      <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                        <Clock size={12} /> {displayPosts[0].readTime}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white group-hover:text-[#2DD4BF] transition-colors duration-300 mb-3 leading-tight">
                      {displayPosts[0].title}
                    </h2>
                    <p className="text-[#94A3B8] leading-relaxed mb-4 text-sm">{displayPosts[0].excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#94A3B8]">
                        {new Date(displayPosts[0].date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                      <span className="text-sm text-[#2DD4BF] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read Article <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </motion.div>
              </Link>
            </ScrollReveal>
          )}

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayPosts.slice(1).map((post, i) => (
              <ScrollReveal key={post.id} delay={i * 60}>
                <Link
                  to={`/blog/${post.id}`}
                  state={{ post }}
                  data-testid={`blog-card-${post.id}`}
                  className="group block rounded-xl border border-white/5 bg-white/[0.02] hover:border-white/10 overflow-hidden transition-all duration-500"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#0F172A] relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-80 transition-all duration-700"
                      loading="lazy"
                    />
                    {/* Audio badge */}
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Volume2 size={10} className="text-[#2DD4BF]" />
                      <span className="text-[9px] text-white">Listen</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span 
                        className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full"
                        style={{ 
                          backgroundColor: `${categoryConfig[post.category]?.color || "#2DD4BF"}15`,
                          color: categoryConfig[post.category]?.color || "#2DD4BF"
                        }}
                      >
                        {post.category}
                      </span>
                      <span className="text-[10px] text-[#94A3B8] flex items-center gap-1">
                        <Clock size={10} /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white group-hover:text-[#2DD4BF] transition-colors duration-300 mb-2 line-clamp-2 leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-xs text-[#94A3B8] line-clamp-2 leading-relaxed mb-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#94A3B8]">
                        {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="text-xs text-[#94A3B8] group-hover:text-[#2DD4BF] flex items-center gap-1 transition-colors">
                        Read <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          {/* View all when filtered */}
          {activeCategory !== "All" && displayPosts.length > 6 && (
            <motion.div 
              className="mt-10 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-sm text-[#94A3B8]">
                Showing {displayPosts.length} articles in {activeCategory}
              </p>
            </motion.div>
          )}
          
          {/* Empty state */}
          {displayPosts.length === 0 && (
            <motion.div 
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-[#94A3B8]">No articles found in this category.</p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
